/**
 * useVOD hook.
 */
import AnalyticsInstance from '@shared/utils/analytics';
import FastEvent from '@shared/classes/fastEvent';
import Focus from '@shared/types/enums/focus';
import Modals from '@shared/types/enums/modals';
import ParentalUtils from '@shared/utils/parental';
import Routes from '@shared/types/enums/routes';
import sendBookmark from '@shared/api/bookmark/request';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import usePauseAds from '@shared/components/utils/hooks/usePauseAds';
import { Coordinates } from '@shared/utils/floatingMenu';
import { PilotType } from '@shared/types/props/pilot';
import { PlayerState, TimeShift } from '@shared/types/enums/player';
import { PlayerTemplateProps } from '@shared/types/props/player';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { routerSelector, setParams } from '@shared/store/slices/router.slice';
import { store } from '@shared/store/store';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { userSelector } from '@shared/store/slices/user.slice';
import { DELAY, DURATION } from '@shared/types/constants/animations';
import { PILOT_DISPLAY_TIME } from '@shared/types/constants/player';
import {
  addKeepWatchingItem,
  updateKeepWatchingItem,
} from '@shared/store/slices/keepWatching.slice';
import {
  clearFocus,
  focusSelector,
  setFocus,
} from '@shared/store/slices/focus.slice';
import {
  clearModal,
  modalSelector,
  setModal,
  setModalParams,
} from '@shared/store/slices/modal.slice';
import {
  closeFloatingMenu,
  openFloatingMenu,
} from '@shared/store/thunks/floatingMenu.thunk';
import {
  mediaSelector,
  toggle as togglePlayer,
  pause,
  play,
  reset as resetMedia,
  seek,
  setRange,
  setVod,
} from '@shared/store/slices/media.slice';
import {
  playerSelector,
  resetPlayer,
  setNextEpisode,
} from '@shared/store/slices/player.slice';

/**
 * Handles the player VOD actions such as floating menu, pilot, next episode, and pause ads.
 */
export default function usePlayer(): PlayerTemplateProps {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useAppDispatch();

  // Selectors
  const { focus } = useAppSelector(focusSelector);
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { error, ready, state, position } = useAppSelector(mediaSelector);
  const { current: modal } = useAppSelector(modalSelector);
  const { nextEpisode } = useAppSelector(playerSelector);
  const { params } = useAppSelector(routerSelector);
  const { parental } = useAppSelector(userSelector);

  // States
  const [asset] = useState(params?.current?.content as FastEvent);
  const [allowPlayback, setAllowPlayback] = useState(false);
  const [pilotVisible, setPilotVisible] = useState(false);

  // Refs
  const assetRef = useRef(asset);
  const enabledRef = useRef<boolean>();
  const errorRef = useRef(error);
  const focusRef = useRef<number | (string | number)[] | null>();
  const mediaPositionRef = useRef(0);
  const modalRef = useRef<Modals | null>();
  const stateRef = useRef<PlayerState>();
  const nextEpisodeRef = useRef(nextEpisode);
  const pilotVisibleRef = useRef<boolean>();
  const pilotTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  assetRef.current = asset;
  enabledRef.current = enabled;
  errorRef.current = error;
  focusRef.current = focus;
  mediaPositionRef.current = position;
  nextEpisodeRef.current = nextEpisode;
  pilotVisibleRef.current = pilotVisible;
  stateRef.current = state;

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Initialize the player: reset the player, set the vod, set the focus, set the range,
   * and build the send function to send the bookmark to the server.
   */
  useEffect(() => {
    // Initialize the player
    dispatch(resetMedia());
    dispatch(setFocus(Focus.PLAYER));
    dispatch(setVod());
    dispatch(
      setRange({
        bookmarks: {
          start: assetRef.current?.startBookmark,
          end: assetRef.current?.endBookmark,
        },
      })
    );

    // Build the send function to send the bookmark to the server
    const send = (forcePosition?: number) => {
      const { media } = store.getState();
      if (asset?.eventId) {
        const mark = forcePosition || media.position;
        sendBookmark(asset?.eventId, mark);
        dispatch(
          updateKeepWatchingItem({
            id: asset.eventId,
            mark,
          })
        );
      }
    };
    return () => {
      // Send the bookmark to the server
      const { keepWatching } = store.getState();
      if (keepWatching.data.find((item) => asset?.eventId == item.id)) {
        send(mediaPositionRef.current);
      } else {
        if (asset?.eventId) {
          dispatch(
            addKeepWatchingItem({
              id: asset?.eventId,
              mark: mediaPositionRef.current,
            })
          );
          send(mediaPositionRef.current);
        }
      }

      // Clean up the player
      clearTimeout(pilotTimeoutRef.current);
      dispatch(closeFloatingMenu());
      dispatch(clearFocus());
      dispatch(clearModal());
      dispatch(resetPlayer());
      dispatch(resetMedia());
    };
  }, []);

  /**
   * Create a playback session for the asset.
   */
  useEffect(() => {
    AnalyticsInstance.createPlaybackSession(asset?.channel, asset);
  }, [asset]);

  /**
   * Handle parental pin modal display logic.
   * Playback is allowed if no modal is displayed.
   */
  useEffect(() => {
    // Dispatch parental pin modal
    if (
      modal === null &&
      parental?.pin &&
      parental?.enabled &&
      ParentalUtils.isTimeRestricted(parental?.timeFrames) &&
      ParentalUtils.isAgeRestricted(asset?.ageCode) &&
      modalRef.current !== Modals.PARENTAL_PIN_GENERIC
    ) {
      modalRef.current = Modals.PARENTAL_PIN_GENERIC;
      store.dispatch(setModalParams({ pop: true }));
      store.dispatch(setModal(Modals.PARENTAL_PIN_GENERIC));
    } else if (!modal) {
      const allowPlayback =
        modal !== null
          ? ![
              Modals.ACTIVATE_ZAPPER,
              Modals.ANONYMOUS_ZAPPER,
              Modals.PARENTAL_PIN_GENERIC,
              Modals.PLAYER_ERROR,
            ].includes(modal)
          : true;
      setAllowPlayback(allowPlayback);
    }
  }, [asset, modal, parental]);

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------

  /**
   * Toggles the floating menu visibility.
   */
  const toggleFloatingMenu = useCallback(
    (open = true) => {
      const { media, router } = store.getState();
      if (
        open &&
        asset &&
        media.state !== PlayerState.IDLE &&
        media.state !== PlayerState.WAITING
      ) {
        const route = {
          asset: asset,
          position: Coordinates.ZAPPER,
          route: Routes.PLAYER,
        };
        const params = router.params.current as {
          content: { keepWatchingMark: number };
        };
        if (params?.content)
          params.content.keepWatchingMark = media.currentTime;
        const stackParams = {
          ...params,
        };
        dispatch(setParams(stackParams));
        dispatch(openFloatingMenu(route));
      } else {
        dispatch(closeFloatingMenu());
      }
    },
    [asset]
  );

  /**
   * Toggles the pilot visibility.
   * Auto-hides after PILOT_DISPLAY_TIME unless floating menu is open or seeking.
   */
  const togglePilot = useCallback((display = true) => {
    if (
      modalRef.current &&
      modalRef.current !== Modals.PARENTAL_PIN_GENERIC &&
      !errorRef.current.code
    ) {
      dispatch(clearModal());
    }
    clearTimeout(pilotTimeoutRef.current);
    if (display) {
      pilotTimeoutRef.current = setTimeout(() => {
        const shouldDisplay =
          focusRef.current === Focus.FLOATING_MENU ||
          stateRef.current === PlayerState.SEEKING ||
          stateRef.current === PlayerState.PAUSE;
        if (shouldDisplay) {
          togglePilot();
        } else {
          setPilotVisible(false);
        }
      }, PILOT_DISPLAY_TIME);
    } else {
      setPilotVisible(false);
      dispatch(setNextEpisode({ display: false }));
    }
    setPilotVisible(display);
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  /**
   * Handles back event.
   * Shows the close player modal if the next episode is displayed or the pilot is not visible and the magic remote is enabled.
   * Hides the pilot.
   */
  const onBack = useCallback(() => {
    // If the next episode is displayed, do nothing
    if (nextEpisodeRef.current.display) return;
    // If the pilot is not visible or the magic remote is enabled, show the close player modal
    if (!pilotVisibleRef.current || enabledRef.current) {
      dispatch(setModal(Modals.CLOSE_PLAYER));
    }
    // Hide the pilot
    togglePilot(false);
  }, []);

  /**
   * Handles magic remote movement to show pilot overlay.
   * Only shows if no modal is currently displayed.
   */
  const onMouseMove = useCallback(() => {
    if (modalRef.current) return;
    togglePilot();
  }, []);

  /**
   * Handles select events.
   * Shows pilot if requested, otherwise toggles the player.
   */
  const onSelect = useCallback(() => {
    // If the pilot is not visible, show it
    if (!pilotVisibleRef.current) {
      togglePilot();
      return;
    }
    // Else, toggle the player
    dispatch(togglePlayer());
  }, []);

  /**
   * Handles horizontal navigation events.
   * Handles timeshift backward and forward.
   */
  const onHorizontalNavigation = useCallback((key: string) => {
    // Timeshift backwards if the key is left or rewind
    if (key === 'LEFT' || key === 'REWIND') {
      // Seek backward to timeshift
      dispatch(seek(TimeShift.BACKWARDS));
    }
    // Timeshift forward / go to channel list if the key is right or fastforward
    else if (key === 'RIGHT' || key === 'FASTFORWARD') {
      dispatch(seek(TimeShift.FORWARD));
    }
  }, []);

  /**
   * Handles vertical navigation events.
   * Handles focus on next episode.
   */
  const onVerticalNavigation = useCallback((key: string) => {
    // If the key is down, do nothing
    if (key === 'DOWN') return;
    // If the key is up, focus on next episode
    if (key === 'UP') {
      if (
        !modalRef.current &&
        stateRef.current !== PlayerState.SEEKING &&
        nextEpisodeRef.current.enabled
      ) {
        dispatch(setFocus(Focus.NEXT_EPISODE));
      }
    }
  }, []);

  // ------------------------------------------------------------
  // Keyboard handlers
  // ------------------------------------------------------------

  /**
   * Handles key down events for player controls and navigation.
   * Handles vertical and horizontal navigation, playback control, and pilot display.
   */
  const keyDownHandler = (key: string) => {
    switch (key) {
      case 'DOWN':
      case 'UP':
        onVerticalNavigation(key);
        break;
      case 'REWIND':
      case 'LEFT':
      case 'FASTFORWARD':
      case 'RIGHT':
        onHorizontalNavigation(key);
        break;
      case 'PAUSE':
        dispatch(pause());
        break;
      case 'PLAY':
        dispatch(play());
        break;
      case 'PLAY_PAUSE':
        dispatch(togglePlayer());
        break;
    }
  };

  /**
   * Handles key press events for player controls and navigation.
   * Opens the floating menu.
   */
  const keyPressHandler = () => {
    toggleFloatingMenu();
  };

  /**
   * Handles key up events for player controls and navigation.
   * Handles back button/key press.
   */
  const keyUpHandler = (key: string) => {
    switch (key) {
      // Close player or display close player modal
      case 'BACK':
        onBack();
        break;
      case 'SELECT':
        onSelect();
        break;
      // Else, If the pilot is not visible, show it
      default:
        togglePilot();
        break;
    }
  };

  // ------------------------------------------------------------
  // Custom hooks
  // ------------------------------------------------------------

  /**
   * Keyboard hook. Handles key down, press, and up events for player controls and navigation.
   */
  useKeyboard({
    focus: Focus.PLAYER,
    keyDownHandler,
    keyPressHandler,
    keyUpHandler,
  });

  /**
   * Pause ads hook. Displays pause ads if enabled.
   */
  usePauseAds({
    asset,
    keyHandler: keyDownHandler,
    useKeyboard: useKeyboard,
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    const displayUI = !(allowPlayback && !ready && !error.code);
    return {
      display: {
        player: allowPlayback,
        spinner: !displayUI,
        ui: displayUI,
      },
      callbacks: {
        container: {
          onMouseMove,
        },
      },
      props: {
        container: {
          delay: DELAY.PLAYER_CONTAINER,
          duration: DURATION.PLAYER_CONTAINER,
        },
        pilot: {
          asset,
          display: pilotVisible,
          type: PilotType.VOD,
        },
        player: {
          asset,
          pilot: pilotVisible,
        },
      },
    };
  }, [asset, allowPlayback, error, pilotVisible, ready, onMouseMove]);
}
